<template>
  <div class="border rounded-lg overflow-hidden">
    <div class="bg-gray-50 px-4 py-3 border-b">
      <div class="grid grid-cols-4 gap-4 text-sm">
        <div>
          <span class="text-gray-500">종목명:</span>
          <span class="ml-2 font-medium">
            {{ ask.bondName || '-' }}
          </span>
        </div>
        <div>
          <span class="text-gray-500">코드:</span>
          <span class="ml-2 font-medium">{{ ask.code || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500">민평:</span>
          <span class="ml-2 font-medium">{{ formatYield(ask.yield) }}%</span>
        </div>
        <div>
          <span class="text-gray-500">3사세전단가:</span>
          <span class="ml-2 font-medium">
            {{ formatPrice(ask.priceBeforeTax) }}
          </span>
        </div>
      </div>

      <!-- 지표 정보 추가 -->
      <div v-if="ask.hasBenchmarkTx && ask.benchmarkBondType && ask.benchmarkMaturityRangeType"
        class="mt-3 pt-3 border-t border-gray-200">
        <div class="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span class="text-gray-500">지표:</span>
            <span class="ml-2 font-medium">
              {{ ask.benchmarkBondType }} {{ ask.benchmarkMaturityRangeType }}
            </span>
          </div>
          <div>
            <span class="text-gray-500">지표거래시각:</span>
            <span class="ml-2 font-medium">
              {{ formatBenchmarkTime(ask.benchmarkTransactionDateTime) }}
            </span>
          </div>
          <div>
            <span class="text-gray-500">지표가격:</span>
            <span class="ml-2 font-medium">
              {{ formatSpread(ask.benchmarkSpread) }}
            </span>
          </div>
          <div>
            <span class="text-gray-500">지표스프레드:</span>
            <span class="ml-2 font-medium">
              {{ formatBenchmarkVsSpread(ask.benchmarkVsSpread) }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            거래시각
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            체결금리
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            스프레드
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            거래량
          </th>
        </tr>
      </thead>
      <tbody class="divide-y bg-white">
        <tr v-for="transaction in sortedTransactions" :key="transaction.id">
          <td class="px-3 py-2 text-sm text-gray-500">
            {{ formatDetailTime(transaction.transactionDateTime) }}
          </td>
          <td class="px-3 py-2 text-sm">
            {{ formatYield(transaction.tradingYield) }}
          </td>
          <td class="px-3 py-2 text-sm">
            <div class="flex items-center gap-1">
              <span :class="{
                'bg-blue-500': isUnderOneYear(transaction.maturityDate)
                  ? transaction.spreadPrice < 0
                  : transaction.spreadBp > 0,
                'bg-red-500': isUnderOneYear(transaction.maturityDate)
                  ? transaction.spreadPrice > 0
                  : transaction.spreadBp < 0,
              }" class="inline-block w-2 h-2 rounded-full"></span>
              <span>{{
                isUnderOneYear(transaction.maturityDate)
                  ? formatWon(transaction.spreadPrice)
                  : formatSpread(transaction.spreadBp)
              }}</span>
            </div>
          </td>
          <td class="px-3 py-2 text-sm">
            {{ formatVolume(transaction.transactionVolume) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { computed } from 'vue';
import { format, differenceInMonths } from 'date-fns';

export default {
  name: 'AskDetailComponent',
  props: {
    ask: {
      type: Object,
      required: true,
    },
    transactions: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const sortedTransactions = computed(() => {
      return [...props.transactions].sort(
        (a, b) => new Date(a.transactionDateTime) - new Date(b.transactionDateTime)
      );
    });

    const formatDetailTime = datetime => {
      if (!datetime) return '-';
      return format(new Date(datetime), 'HH:mm:ss');
    };

    const isUnderOneYear = maturityDate => {
      if (!maturityDate) return false;
      const monthsDiff = differenceInMonths(new Date(maturityDate), new Date());
      return monthsDiff <= 12;
    };

    const formatWon = price => {
      if (price === null || price === undefined || price === '-') return '-';
      // 100만원:1원 비율로 변환하여 소수점 둘째자리까지 표시
      const convertedPrice = price / 1000000;
      return `${convertedPrice > 0 ? '+' : ''}${convertedPrice.toFixed(2)}원`;
    };

    const formatPrice = price => {
      if (price === null || price === undefined || price === '-') return '-';
      const convertedPrice = Math.floor(price / 1000000);
      return `${convertedPrice}원`;
    };

    const formatSpread = spread => {
      if (!spread) return '0.0bp';
      const spreadValue = parseFloat(spread).toFixed(1);
      return `${spreadValue > 0 ? '+' : ''}${spreadValue}bp`;
    };

    const formatVolume = volume => {
      if (!volume) return '-';
      // 원화를 억 단위로 변환 (1억 = 100,000,000원)
      const volumeInEok = volume / 100000000;
      return `${volumeInEok.toFixed(1)}억`;
    };

    const formatYield = yieldValue => {
      if (!yieldValue || yieldValue === '-') return '-';
      return parseFloat(yieldValue).toFixed(3);
    };

    const formatBenchmarkTime = datetime => {
      if (!datetime) return '-';
      return format(new Date(datetime), 'HH:mm:ss');
    };

    const formatBenchmarkVsSpread = spread => {
      if (!spread) return '-';
      const spreadValue = parseFloat(spread).toFixed(1);
      return `${spreadValue > 0 ? '+' : ''}${spreadValue}bp`;
    };

    return {
      sortedTransactions,
      formatDetailTime,
      isUnderOneYear,
      formatWon,
      formatPrice,
      formatSpread,
      formatVolume,
      formatYield,
      formatBenchmarkTime,
      formatBenchmarkVsSpread,
    };
  },
};
</script>
